<script lang="ts">
  import { onMount } from "svelte"
  import { slide } from "svelte/transition"
  import { xhr, toast } from "$app/utils"
  import { ChevronDown, ChevronRight } from "lucide-svelte"
  import DepartmentGoals from "./DepartmentGoals.svelte"
  import { appStore } from "$app/store.svelte"
  import consumer from "$app/channels/consumer"
  import ScrollTo from "$app/Pages/Plans/Meetings/ScrollTo.svelte"
  import { toast as sToast } from "@zerodevx/svelte-toast"
  import Tiptap from "$app/components/Tiptap.svelte"
  import { onlyTextButtons } from "$app/components/Tiptap/buttons"

  const plan = appStore.plan

  interface Props {
    plan_visions: PlanVisionType
  }

  let { plan_visions }: Props = $props()

  let expanded_vision = $state(true)
  let expanded_goals = $state(true)

  let vision_changed = $state(false)
  let initialVision = $state(plan_visions?.next_quarter?.vision || "")
  let prev_vision_tab = $state("quarter")
  let vision = $derived(JSON.parse(JSON.stringify(initialVision)))
  const channel_id = appStore.channel_id

  const dates = appStore.dates

  function updateExpandedDepts(expanded: boolean): void {
    appStore.departments.forEach((d) => (d._selected = expanded))
    //departments = departments
  }

  let save_timeout = null
  async function xhrSaveVision(): Promise<void> {
    clearTimeout(save_timeout)
    if (vision !== initialVision) {
      try {
        xhr.patch(
          `/plans/${plan.id}/visions/${plan_visions.next_quarter.id}.json?channel=${channel_id}`,
          { plan_vision: { vision } }
        )

        vision_changed = false
      } catch (e) {
        console.error(e)
        toast("Error saving vision", "error")
      }
    }
  }

  function saveVision() {
    clearTimeout(save_timeout)
    save_timeout = setTimeout(() => {
      xhrSaveVision()
    }, 600)
  }

  export function valid(): boolean {
    return true
  }

  /** BEGIN Goal Methods **/
  function updateGoals(data: any[], msg = "", showToast = true): void {
    const goalsData = data as GoalType[]
    let updatedGoal = null
    goalsData.forEach((goal) => {
      const idx = appStore.goals.findIndex((g) => g.id === goal.id)
      if (idx > -1) {
        updatedGoal = goal
        goal._selected = false
        appStore.goals[idx] = { ...appStore.goals[idx], ...goal }
      }
    })

    if (showToast && msg.length > 0) {
      setToast("goal", updatedGoal, msg)
    }
  }

  function addGoal(data: any, msg = "", showToast = true): void {
    const newGoal = {
      ...data,
      toggle: false,
      selected: false
    } as GoalType

    appStore.goals = [...appStore.goals, newGoal]

    if (data.tasks?.length > 0) {
      const newTasks = data.tasks.map((t) => {
        return { ...t, selected: false }
      })

      appStore.tasks = [...appStore.tasks, ...newTasks]
    }

    if (showToast && msg.length > 0) {
      setToast("goal", data, `Goal ${data.name} was created`)
    }
  }

  function deleteGoal(data: any, showToast = true): void {
    const idx = appStore.goals.findIndex((g) => g.id === data.id)
    appStore.goals.splice(idx, 1)

    appStore.goals = [...appStore.goals]
    if (showToast) {
      toast(`Goal ${data.name} has been deleted`, "success")
    }
  }
  /** End of Goal Methods **/
  /*************************/

  /** BEGIN Tasks Methods **/
  function updateTasks(data: TaskTypeE[], msg = "", showToast = true): void {
    const tasksCopy = [...appStore.tasks]
    data.forEach((task) => {
      task.expected_at = new Date(`${task.expected_at}T00:00:00`)
      task._selected = false
      const idx = tasksCopy.findIndex((t) => t.id === task.id)

      if (idx > -1) {
        tasksCopy[idx] = { ...appStore.tasks[idx], ...task }
      }
    })

    appStore.tasks = []
    setTimeout(() => {
      appStore.tasks = [...tasksCopy]
    })

    if (showToast && msg.length > 0) {
      setToast("task", data[0], msg)
    }
  }

  function addTask(data: any, showToast = true): void {
    const newTask = {
      ...data.task_created,
      selected: false
    } as TaskTypeE

    appStore.tasks = [...appStore.tasks, newTask]

    if (showToast) {
      setToast("task", newTask, `Task "${newTask.name}" has been created`)
    }
  }

  function deleteTask(data: any, showToast = true): void {
    const idx = appStore.tasks.findIndex((t) => t.id === data.id)
    appStore.tasks.splice(idx, 1)
    appStore.tasks = [...appStore.tasks]

    if (showToast) {
      toast(`Task ${data.name} has been deleted`, "success")
    }
  }
  /** END of Tasks Methods **/
  /**************************/
  function actionCableChannel(): void {
    consumer.subscriptions.create(
      {
        channel: "GoalsChannel",
        room: appStore.meeting.uniq_id
      },
      {
        connected() {
          console.log("%cConnected to meetings goals channel", "color: green")
        },
        disconnected() {},
        received(data: any) {
          if (data.goal_updated) {
            updateGoals(
              [data.goal_updated],
              `Goal ${data.goal_updated.name} has been updated`,
              data.user_id !== appStore.current_user.id
            )
            return
          }
          if (data.goals_sorted) {
            updateGoals(
              data.goals_sorted,
              "Goals have been sorted",
              data.user_id !== appStore.current_user.id
            )
            return
          }
          if (data.goals_moved) {
            updateGoals(
              data.goals_moved,
              "Goals department re-assigned",
              data.user_id !== appStore.current_user.id
            )
            return
          }
          if (data.goal_created) {
            addGoal(
              data.goal_created,
              "New goal has been created",
              data.user_id !== appStore.current_user.id
            )
            return
          }
          if (data.goal_duplicated) {
            addGoal(
              data.goal_duplicated,
              "Goal has been duplicated",
              data.user_id !== appStore.current_user.id
            )
            return
          }
          if (data.goal_deleted) {
            deleteGoal(
              data.goal_deleted,
              data.user_id !== appStore.current_user.id
            )
            return
          }

          // tasks
          if (data.task_updated) {
            updateTasks(
              [data.task_updated],
              "Task has been updated",
              data.user_id !== appStore.current_user.id
            )
            return
          }
          if (data.tasks_sorted) {
            updateTasks(
              data.tasks_sorted,
              "Tasks have been sorted",
              data.user_id !== appStore.current_user.id
            )
            return
          }
          if (data.task_created) {
            addTask(data, data.user_id !== appStore.current_user.id)
            return
          }
          if (data.task_deleted) {
            deleteTask(
              data.task_deleted,
              data.user_id !== appStore.current_user.id
            )
            return
          }
          if (data.plan_vision) {
            if (initialVision !== data.plan_vision.vision) {
              initialVision = data.plan_vision.vision
              toast("Vision updated", "success")
            }
            return
          }
        }
      }
    )
  }

  function setToast(id: string, data: any, msg: string): void {
    sToast.push({
      component: {
        src: ScrollTo,
        props: {
          message: msg,
          id: id,
          data: data
        }
      },
      duration: 8000,
      dismissable: true,
      theme: {
        "--toastBackground": "white"
      }
    })
  }

  function toggleDepartments(toggle: boolean): void {
    appStore.departments.forEach((d) => (d._selected = toggle))
    appStore.departments = appStore.departments
  }

  onMount(() => {
    actionCableChannel()
    if (appStore.departments[0]) {
      appStore.departments[0]._selected = true
    }
  })
</script>

<h2
  class="text-xl text-center w-full py-4 font-semibold bg-indigo-50 text-indigo-800 rounded-sm shadow px-4"
>
  Review Quarter Vision & Goals
</h2>

<!-- svelte-ignore a11y_click_events_have_key_events -->
<div
  class="pb-4 flex justify-start mt-6"
  onclick={() => (expanded_vision = !expanded_vision)}
  role="button"
  tabindex="0"
>
  <button
    class="flex items-center justify-start text-xl font-semibold hover:cursor-pointer focus:outline-none"
    type="button"
  >
    {#if expanded_vision}
      <ChevronDown class="text-gray-500 w-5 h-5" />
    {:else}
      <ChevronRight class="text-gray-500 w-5 h-5" />
    {/if}
    1. Set vision for {dates.next_quarter}
  </button>
</div>

{#if expanded_vision}
  <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
    <div class="bg-gray-50">
      <div class="overflow-visible pb-2">
        <div class="hidden sm:block">
          <div class="border-b border-gray-200 flex justify-between">
            <nav class="-mb-px flex tabs px-4" aria-label="Tabs">
              <button
                onclick={() => {
                  prev_vision_tab = "quarter"
                }}
                class:active={prev_vision_tab === "quarter"}
                class="tab"
              >
                <div class="px-4 whitespace-nowrap">
                  Quarterly Vision for {dates.prev_quarter}
                </div>
              </button>
              <button
                onclick={() => {
                  prev_vision_tab = "annual"
                }}
                class:active={prev_vision_tab === "annual"}
                class="tab"
              >
                <div class="px-4 whitespace-nowrap">
                  Annual Vision for {dates.year}
                </div>
              </button>
            </nav>
          </div>
        </div>
      </div>

      <div class="w-full h-96 overflow-y-auto">
        <p class="text-gray-600 px-6 py-4 ProseMirror">
          {#if prev_vision_tab === "quarter"}
            {@html plan_visions.prev_quarter?.vision || "Vision not set"}
          {:else if prev_vision_tab === "annual"}
            {@html plan_visions.curr_year?.vision ||
              "Annual Vision was not Set"}
          {/if}
        </p>
      </div>
    </div>
    <div
      class="bg-gray-50 border relative"
      class:border-red-400={vision_changed}
    >
      <h2 class="text-xl text-center font-bold mb-2 mt-4">
        Set Quarterly Vision for {dates.next_quarter}
      </h2>

      <div class="block">
        <div class="text-left px-2 inline-block w-full">
          <div
            class="border border-gray-100 rounded-lg"
            class:border-red-300={initialVision !== vision}
          >
            <Tiptap
              content={initialVision}
              onblur={saveVision}
              onupdate={({ html }) => (vision = html)}
              placeholder="Enter your vision"
              class="h-64"
              buttons={onlyTextButtons}
            />
          </div>
        </div>
      </div>

      <div
        class="w-full px-2 py-2 shadow absolute bottom-0 flex items-end justify-end"
      >
        <button
          class="btn btn-outline px-6 text-lg font-bold"
          class:success={initialVision !== vision}
          onclick={xhrSaveVision}
        >
          Save Vision
        </button>
      </div>
    </div>
  </div>
{/if}

<div class="mt-12 pb-4 flex justify-start items-center">
  <button
    class="flex items-center justify-start text-xl font-semibold hover:cursor-pointer focus:outline-none expand-button"
    onclick={() => (expanded_goals = !expanded_goals)}
    data-expanded={expanded_goals}
    data-name="departments-expander"
  >
    {#if expanded_goals}
      <ChevronDown class="text-gray-500 w-5 h-5" />
    {:else}
      <ChevronRight class="text-gray-500 w-5 h-5" />
    {/if}
    2. Set goals for {dates.next_quarter}
  </button>

  <div class="ml-8">
    {#if expanded_goals}
      <div class="p-1 flex justify-center w-full">
        <button
          class="bg-gray-100 text-sm focus:outline-none border border-gray-200 py-2 px-4 rounded-sm hover:bg-blue-100"
          type="button"
          data-name="close-all-departments-button"
          onclick={() => toggleDepartments(false)}
        >
          Close All Departments
        </button>

        <button
          class="bg-gray-100 text-sm ml-2 focus:outline-none border border-gray-200 py-2 px-4 rounded-sm hover:bg-blue-100"
          type="button"
          data-name="open-all-departments-button"
          onclick={() => toggleDepartments(true)}
        >
          Open All Departments
        </button>
      </div>
    {/if}
  </div>
</div>

{#if expanded_goals}
  <div transition:slide={{ duration: 300 }}>
    <!--Prev-->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-8 lg:gap-y-0">
      <div>
        <h2 class="text-xl text-center mb-4 h-8">
          {dates.prev_quarter} Goals
        </h2>

        <div
          class="w-full bg-gray-50 border-gray-100 shadow-sm goals-container"
          data-period="current"
          data-name="current-quarter"
        >
          {#each appStore.departments as department (department.id)}
            <DepartmentGoals {department} period="prev" />
          {/each}
        </div>
      </div>

      <!--Next-->
      <div>
        <div class="flex justify-center items-center mb-4 h-8">
          <h2 class="text-xl text-center mr-3">
            {dates.next_quarter} Goals
          </h2>
        </div>

        <div
          class="w-full bg-white border-gray-100 shadow-sm goals-container"
          data-period="next"
          data-name="next-quarter"
        >
          {#each appStore.departments as department}
            <DepartmentGoals {department} period="next" />
          {/each}
        </div>
      </div>
    </div>
  </div>
{/if}

<style>
  :global(._toastBtn::after) {
    color: gray;
  }
</style>
